/**
 * AshfallSkillEffect — Fire tree backdraft-path apotheosis: channeled fire rain.
 *
 * A 3-second channel, after which fire rains across a targeted 5-tile radius at
 * long range. Heavy direct damage, full burning DoT on all enemies in the blast,
 * and the impact zone fills with dense smoke for 6 seconds.
 *
 * **Activation:** Press begins the channel. Player cannot move or take other
 * actions during channel (game layer enforces this by checking `isActive()`).
 * Target position locks at channel start from `getTargetPosition()`.
 *
 * **Channel (3000ms):** Player is stationary and vulnerable. VFX: glow, embers
 * rising. The channel IS the risk window — three seconds of standing still while
 * reality decides whether to let you do this.
 *
 * **Interruption:** If `deactivate()` is called during channel (damage taken,
 * stun, etc.), no burst fires. Skill goes on full cooldown anyway.
 *
 * **Channel complete:** Instant fire burst at locked target position:
 *   - `dealDamage` to all enemies within `impactRadiusTiles` (5).
 *   - `applyEnemyStatus('burning', ...)` on each hit enemy.
 *   - `ZoneSystem.create` smoke_cloud (circle, 5-tile radius, 6000ms).
 *
 * **Smothered interaction:** Post-impact smoke cloud duration gains
 * `smokeCloudDurationBonus` from the fire tree passive accumulator
 * (6000 + 1000 = 7000ms when Smothered is active).
 *
 * **Apotheosis gate:** Both `choke_passive` (Smothered) and `cauterize_passive`
 * (Feeding Flame) are parents — OR-gate (unlock when any parent Fork-2
 * passive is complete).
 *
 * @see fire-spec.md § Apotheosis: Ashfall
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone create config (smoke cloud spawned at impact site)
// ---------------------------------------------------------------------------

export interface AshfallSmokeConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: 'circle';
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly detectionReduction: number;
  readonly sourceSkillId: string;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AshfallDeps {
  readonly resolver: {
    /**
     * Query a tree-wide passive accumulator. Smothered (choke_passive) registers
     * `smokeCloudDurationBonus` under treeId `'fire'` with `'sum'` aggregation.
     */
    getTreePassiveEffect(treeId: string, key: string, aggregation: 'sum' | 'product'): number;
  };

  /**
   * Return the target position for the fire rain impact. The game layer
   * determines this from player facing direction + range at activation time.
   * The position is locked when the channel begins.
   */
  readonly getTargetPosition: () => { x: number; y: number };

  /** Return entity IDs of all enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];

  /** Deal direct damage to a target. */
  readonly dealDamage: (entityId: string, damage: number) => void;

  /**
   * Apply a status effect (burning) to an enemy entity.
   * Source-tagged per ADR-066 for DoT coexistence.
   */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;

  readonly zones: {
    create(config: AshfallSmokeConfig): string;
  };

  /** Break stealth / hide state on activation. */
  readonly breakHide: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AshfallConfig {
  /** Post-burst cooldown in ms. Very long tier = 4000ms. */
  readonly cooldownMs: number;
  /** Channel duration in ms before the burst fires. */
  readonly channelDurationMs: number;
  /** Impact radius in tiles (damage + burning + smoke cloud). */
  readonly impactRadiusTiles: number;
  /** Direct damage dealt to each enemy in the impact radius. */
  readonly impactDamage: number;
  /** Duration of burning DoT applied to each hit enemy. */
  readonly burningDurationMs: number;
  /** Duration of the post-impact smoke cloud (before Smothered bonus). */
  readonly smokeDurationMs: number;
  /** Detection reduction applied by the post-impact smoke cloud. */
  readonly smokeDetectionReduction: number;
}

const DEFAULT_CONFIG: AshfallConfig = {
  cooldownMs: 4000,             // very_long cooldown tier
  channelDurationMs: 3000,      // 3 seconds of standing still
  impactRadiusTiles: 5,         // spec: 5-tile radius
  impactDamage: 20,             // "heavy damage" — tunable via config
  burningDurationMs: 3000,      // standard full burning duration
  smokeDurationMs: 6000,        // spec: 6 seconds of dense smoke
  smokeDetectionReduction: 0.3, // matches Smoke passive's cloud value
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AshfallSkillEffect implements SkillEffect {
  readonly skillId = 'ashfall';
  readonly type = 'active' as const;
  readonly activationMode = 'channel' as const;

  private readonly _deps: AshfallDeps;
  private readonly _config: AshfallConfig;

  private _cooldownRemaining = 0;
  private _channelRemaining = 0;
  private _isChanneling = false;
  /** Target position locked at channel start. */
  private _targetPos: { x: number; y: number } | null = null;

  // Counters exposed for scenario assertions
  private _activations = 0;
  private _interrupts = 0;

  constructor(deps: AshfallDeps, config: Partial<AshfallConfig> = {}) {
    this._deps = deps;
    this._config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Accessors ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get interrupts(): number { return this._interrupts; }
  get channelRemaining(): number { return this._channelRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._isChanneling;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._deps.breakHide();

    // Lock target position at channel start — the fire will land here
    // regardless of where the player looks during the channel.
    this._targetPos = this._deps.getTargetPosition();
    this._isChanneling = true;
    this._channelRemaining = this._config.channelDurationMs;
    return true;
  }

  /**
   * Interrupt the channel. No burst fires. Skill goes on full cooldown.
   * Called by the game layer when the player takes damage, is stunned,
   * or otherwise interrupted during the 3-second window.
   */
  deactivate(): void {
    if (!this._isChanneling) return;

    this._isChanneling = false;
    this._channelRemaining = 0;
    this._targetPos = null;
    this._cooldownRemaining = this._config.cooldownMs;
    this._interrupts++;
  }

  isActive(): boolean { return this._isChanneling; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._isChanneling) return;

    this._channelRemaining -= delta;
    if (this._channelRemaining <= 0) {
      this._completeChannel();
    }
  }

  destroy(): void {
    this._isChanneling = false;
    this._cooldownRemaining = 0;
    this._channelRemaining = 0;
    this._targetPos = null;
  }

  // ---------------------------------------------------------------------------
  // Channel completion — the sky opens
  // ---------------------------------------------------------------------------

  private _completeChannel(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const target = this._targetPos!;

    this._isChanneling = false;
    this._channelRemaining = 0;
    this._targetPos = null;

    // Deal impact damage and apply burning to every enemy in the blast radius.
    // The orb is instantaneous — it doesn't travel, it arrives.
    const enemies = this._deps.getEnemiesInRadius(
      target.x,
      target.y,
      this._config.impactRadiusTiles,
    );

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, this._config.impactDamage);
      this._deps.applyEnemyStatus(enemyId, 'burning', {
        durationMs: this._config.burningDurationMs,
        sourceSkillId: 'ashfall',
      });
    }

    // Post-impact smoke cloud at the target site.
    // Smothered (choke_passive) bonus extends all smoke cloud durations.
    const smotheredBonusMs = this._deps.resolver.getTreePassiveEffect(
      'fire',
      'smokeCloudDurationBonus',
      'sum',
    );

    this._deps.zones.create({
      id: `ashfall-smoke-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'smoke_cloud',
      position: { x: target.x, y: target.y },
      shape: 'circle',
      radiusTiles: this._config.impactRadiusTiles,
      durationMs: this._config.smokeDurationMs + smotheredBonusMs,
      detectionReduction: this._config.smokeDetectionReduction,
      sourceSkillId: 'ashfall',
    });

    this._cooldownRemaining = this._config.cooldownMs;
    this._activations++;
  }
}
